import React from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Download, File, Send } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import * as Query from '../../api/gql/query'
import { API_URL } from '../../utils'
import logo from '../../assets/img/logo_signin.png'

interface TransmittalItem {
  id: string
  document: {
    id: string
    name: string
  }
  revision: {
    id: string
    version: number
    downloadUrl: string
    file?: {
      id: string
      filename: string
      mimetype: string
      size: number
    }
  }
}

const formatFileSize = (bytes: number) => {
  if (!bytes) return '-'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

const TransmittalAccessPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const { t } = useTranslation()

  const { data, loading, error } = useQuery(Query.ACCESS_DOC_TRANSMITTAL, {
    variables: { token },
    skip: !token,
    fetchPolicy: 'network-only',
  })

  const result = data?.accessDocTransmittal
  const transmittal = result?.transmittal

  // Bulk download goes through the transmittal-token-scoped zip route; single
  // files already use presigned revision.downloadUrl. The raw /download routes
  // are auth-only and unreachable from this public page.
  const downloadableCount = (transmittal?.items || []).filter(
    (item: TransmittalItem) => item.revision.file?.id,
  ).length
  const zipUrl =
    downloadableCount > 0 && token ? `${API_URL}/download/transmittal/${token}/zip` : null

  return (
    <div className="min-h-screen bg-muted py-10">
      <div className="mx-auto w-full max-w-3xl px-4">
        <div className="mb-6 flex justify-center">
          <img src={logo} alt="Urkla" className="h-20" />
        </div>

        {loading && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center gap-3 py-12">
              <Spinner />
              <p className="text-muted-foreground">{t('Shared_loading')}</p>
            </CardContent>
          </Card>
        )}

        {!loading && (error || (result && !result.success)) && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <AlertTriangle className="h-12 w-12 text-[hsl(var(--warning))]" />
              <h4 className="text-lg font-semibold">{t('Shared_error_title')}</h4>
              <p className="text-muted-foreground">
                {result?.error === 'Transmittal not found'
                  ? t('Shared_error_not_found')
                  : result?.error === 'Transmittal has expired'
                  ? t('Shared_error_expired')
                  : result?.error || t('Shared_error_not_found')}
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && result?.success && transmittal && (
          <Card>
            <CardHeader>
              <div className="flex items-start gap-3">
                <Send className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div className="min-w-0">
                  <CardTitle className="text-xl">{transmittal.name}</CardTitle>
                  {transmittal.reference && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t('DocControl_reference')}: {transmittal.reference}
                    </p>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {transmittal.description && (
                <p className="text-muted-foreground">{transmittal.description}</p>
              )}

              <div className="flex items-center justify-between gap-2">
                <h6 className="text-sm font-semibold">
                  {t('DocControl_documents')} ({transmittal.items?.length || 0})
                </h6>
                {zipUrl && downloadableCount > 1 && (
                  <Button asChild variant="outline" size="sm">
                    <a href={zipUrl} download>
                      <Download className="h-4 w-4" />
                      {t('Shared_download_all')}
                    </a>
                  </Button>
                )}
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('DocControl_document')}</TableHead>
                      <TableHead className="w-[100px]">{t('DocControl_version')}</TableHead>
                      <TableHead className="w-[100px]">{t('DocControl_size')}</TableHead>
                      <TableHead className="w-[140px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(transmittal.items || []).map((item: TransmittalItem) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <File className="h-4 w-4 text-muted-foreground" />
                            <span>{item.document.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="default" className="rounded-full">
                            v{item.revision.version}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {item.revision.file?.size
                              ? formatFileSize(item.revision.file.size)
                              : '-'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {item.revision.downloadUrl && (
                            <Button asChild size="sm">
                              <a
                                href={item.revision.downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={item.revision.file?.filename}
                              >
                                <Download className="h-4 w-4" />
                                {t('Common_download')}
                              </a>
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="mt-6 text-center text-xs text-muted-foreground">
          {t('Shared_footer_text')}
        </p>
      </div>
    </div>
  )
}

export default TransmittalAccessPage
